/**
 * Custom element for the "Mengenrabatt & Bundles" product block (snippets/quantity-discount.liquid).
 *
 * Option 1 (single product) keeps the default behavior. Option 2 multiplies the submitted quantity
 * (the actual 10% discount is applied in cart by an automatic discount) and swaps the price block
 * content with the pre-rendered double pack prices (2x price minus the discount, 2x price struck
 * through, discounted unit price and the percentage sale badge). Option 3 is a plain link to
 * another product.
 */
(function () {
  if (window.customElements.get('quantity-discount')) {
    return;
  }

  class QuantityDiscount extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this._multiplier = 1;
      this._originals = [];
      this._badge = null;
      this._hiddenBadges = [];
      this._form = document.forms[this.getAttribute('form')];

      this.querySelectorAll('input[data-qd-option]').forEach((input) => {
        input.addEventListener('change', this._onOptionChange.bind(this, input), { signal });
      });

      this._reflectSelectedOption();

      if (this._form) {
        // "product-form" serializes the form with new FormData(form) on submit, which fires this
        // event synchronously and lets us multiply the quantity without touching the visible input.
        this._form.addEventListener('formdata', this._onFormData.bind(this), { signal });
        this._form.addEventListener('variant:change', this._onVariantChange.bind(this), { signal });
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get _mainPriceList() {
      return this.closest('.product-info')?.querySelector('[data-block-type="price"] price-list');
    }

    // The price lists to override, each paired with the template matching its size variant
    _priceTargets() {
      const targets = [];
      const mainPriceList = this._mainPriceList;

      if (mainPriceList) {
        targets.push({ element: mainPriceList, template: 'price' });
      }

      const stickyBar = document.getElementById(`${this.getAttribute('form')}-sticky-bar`);
      const stickyPriceList = stickyBar?.querySelector('price-list');

      if (stickyPriceList) {
        targets.push({ element: stickyPriceList, template: 'price-sm' });
      }

      return targets;
    }

    // Fallback for browsers without :has() support
    _reflectSelectedOption() {
      this.querySelectorAll('.quantity-discount__option').forEach((option) => {
        const input = option.querySelector('input[data-qd-option]');
        option.classList.toggle('quantity-discount__option--selected', !!input?.checked);
      });
    }

    _onOptionChange(input) {
      this._reflectSelectedOption();

      if (input.value === '2') {
        this._multiplier = parseInt(this.getAttribute('quantity-multiplier'), 10) || 2;
        this._applyPriceOverride();
      } else {
        this._multiplier = 1;
        this._removePriceOverride();
      }
    }

    _onFormData(event) {
      if (this._multiplier <= 1) {
        return;
      }

      const quantity = parseInt(event.formData.get('quantity'), 10) || 1;
      event.formData.set('quantity', quantity * this._multiplier);
    }

    _onVariantChange() {
      // The pre-rendered price overrides only match the variant rendered on the server, and the theme
      // has just re-rendered the price block, so fall back to the single product option.
      const defaultOption = this.querySelector('input[data-qd-option][value="1"]');

      if (defaultOption) {
        defaultOption.checked = true;
      }

      this._reflectSelectedOption();
      this._multiplier = 1;
      this._originals = [];
      this._badge = null;
      this._hiddenBadges = [];
    }

    _applyPriceOverride() {
      if (this._originals.length > 0) {
        return;
      }

      this._priceTargets().forEach(({ element, template }) => {
        const priceTemplate = this.querySelector(`template[data-qd-template="${template}"]`);

        if (!priceTemplate) {
          return;
        }

        this._originals.push({ element, html: element.innerHTML });
        element.replaceChildren(priceTemplate.content.cloneNode(true));
      });

      const mainPriceList = this._mainPriceList;
      const badgeTemplate = this.querySelector('template[data-qd-template="badge"]');

      if (mainPriceList && badgeTemplate && !this._badge) {
        this._badge = badgeTemplate.content.firstElementChild.cloneNode(true);
        mainPriceList.insertAdjacentElement('afterend', this._badge);
      }

      this._hiddenBadges = Array.from(
        this.closest('.product-info')?.querySelectorAll('[data-block-type="price"] on-sale-badge') || []
      ).filter((badge) => !badge.closest('[data-qd-badge]'));
      this._hiddenBadges.forEach((badge) => {
        badge.style.display = 'none';
      });
    }

    _removePriceOverride() {
      this._originals.forEach(({ element, html }) => {
        if (element.isConnected) {
          element.innerHTML = html;
        }
      });
      this._originals = [];

      this._badge?.remove();
      this._badge = null;
      this._hiddenBadges.forEach((badge) => {
        badge.style.display = '';
      });
      this._hiddenBadges = [];
    }
  }

  window.customElements.define('quantity-discount', QuantityDiscount);
})();
